import { en, type Messages } from './en.js';
import { ptBR } from './pt-BR.js';
import { es } from './es.js';

export type { Messages };
export type AppLocale = 'en' | 'pt-BR' | 'es';
export const SUPPORTED_LOCALES: readonly AppLocale[] = ['en', 'pt-BR', 'es'];
export const DEFAULT_LOCALE: AppLocale = 'en';
export const CATALOG_VERSION = '2026-10-06.1';

export const catalogs: Record<AppLocale, Messages> = { en, 'pt-BR': ptBR, es };

/** Browser/profile language to supported locale: pt-* → pt-BR, es-* → es, otherwise en. */
export function resolveLocale(tag: string | null | undefined): AppLocale {
  const lower = (tag ?? '').toLowerCase();
  if (lower.startsWith('pt')) return 'pt-BR';
  if (lower.startsWith('es')) return 'es';
  return 'en';
}

/** Flattens a nested catalog into dotted keys (used by tests and server-side templates). */
export function flatten(messages: object, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(messages)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') out[path] = value;
    else Object.assign(out, flatten(value as object, path));
  }
  return out;
}

const flatCache = new Map<AppLocale, Record<string, string>>();

/** Minimal server-side translator with {{name}} interpolation and English fallback. */
export function translate(locale: AppLocale, key: string, values: Record<string, string | number> = {}): string {
  if (!flatCache.has(locale)) flatCache.set(locale, flatten(catalogs[locale]));
  if (!flatCache.has('en')) flatCache.set('en', flatten(en));
  const template = flatCache.get(locale)![key] ?? flatCache.get('en')![key] ?? key;
  return template.replace(/\{\{(\w+)\}\}/g, (_, name: string) => String(values[name] ?? `{{${name}}}`));
}

/** Language names for "machine translated from {{language}}" labels, per UI locale. */
export function languageName(code: string, uiLocale: AppLocale): string {
  if (!code || code === 'und') return uiLocale === 'pt-BR' ? 'idioma desconhecido' : uiLocale === 'es' ? 'idioma desconocido' : 'unknown language';
  try {
    return new Intl.DisplayNames([uiLocale], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}

export { en, ptBR, es };
